import React, { useState, useEffect } from 'react';
import { Search, Phone, MessageSquare, Mail, Linkedin, RotateCcw, Play, Pause } from 'lucide-react';
import AgentActionVisualizer from './AgentActionVisualizer';

interface TaskChip {
  icon: React.ReactNode;
  text: string;
}

interface AgentStage {
  id: number;
  name: string;
  image: string;
  oneLiner: string;
  railLabel: string;
  chips: TaskChip[];
}

const HowItWorksSection: React.FC = () => {
  const [currentStage, setCurrentStage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  const stages: AgentStage[] = [
    {
      id: 1,
      name: 'Lead Gen Agent',
      image: '/leadgenagent.png',
      oneLiner: 'Finds, qualifies and verifies prospects at scale',
      railLabel: 'Lead Discovery',
      chips: []
    },
    {
      id: 2,
      name: 'Call Agent',
      image: '/callagent.png',
      oneLiner: 'Makes personalized outbound calls, qualifies leads, and books meetings',
      railLabel: 'Outbound Call',
      chips: []
    },
    {
      id: 3,
      name: 'WhatsApp Agent',
      image: '/whatsappagent.png',
      oneLiner: 'Starts and manages real-time conversations that convert prospects into meetings',
      railLabel: 'WhatsApp',
      chips: []
    },
    {
      id: 4,
      name: 'Email Agent',
      image: '/emailagent.png',
      oneLiner: 'Delivers high-impact email campaigns that engage, qualify, and convert prospects',
      railLabel: 'Email',
      chips: []
    },
    {
      id: 5,
      name: 'LinkedIn Agent',
      image: '/linkedinagent.png',
      oneLiner: 'Builds relationships and generates leads through smart, personalized LinkedIn outreach',
      railLabel: 'LinkedIn',
      chips: []
    },
    {
      id: 6,
      name: 'Follow Up Agent',
      image: '/followupagent.png',
      oneLiner: 'Nurtures and re-engages prospects with perfect timing and personalized sequences',
      railLabel: 'Follow-up',
      chips: []
    }
  ];

  // Auto-advance stages
  useEffect(() => {
    if (isPaused) return;

    const stageInterval = setInterval(() => {
      setCurrentStage((prev) => (prev + 1) % stages.length);
      setProgress(0);
    }, 4000);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 0;
        return prev + (100 / 400); // 4000ms / 10ms intervals
      });
    }, 10);

    return () => {
      clearInterval(stageInterval);
      clearInterval(progressInterval);
    };
  }, [isPaused, stages.length]);

  const handleStageClick = (stageIndex: number) => {
    setCurrentStage(stageIndex);
    setProgress(0);
  };

  const handlePauseToggle = () => {
    setIsPaused(!isPaused);
  };

  const handleReplay = () => {
    setCurrentStage(0);
    setProgress(0);
    setIsPaused(false);
  };

  const currentAgent = stages[currentStage];

  const getAgentIcon = (index: number) => {
    const icons = [Search, Phone, MessageSquare, Mail, Linkedin, RotateCcw];
    const IconComponent = icons[index];
    return <IconComponent className="w-4 h-4" />;
  };

  return (
    <section className="relative py-8 sm:py-12 lg:py-16 px-4 sm:px-6">
      {/* Soft vignette overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-bg-deep/20 to-bg-deep/40 pointer-events-none" />
      
      <div className="w-full max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-text-primary font-black text-2xl sm:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4 tracking-tight">
            Your AI Sales Team, In Action
          </h2>
          <p className="text-text-secondary text-base sm:text-lg lg:text-xl xl:text-2xl max-w-3xl mx-auto leading-relaxed px-4">
            Smart targeting, human-like outreach, relentless follow-up.
          </p>
          <div className="w-16 sm:w-20 lg:w-24 h-1 bg-gradient-brand mx-auto mt-4 sm:mt-6"></div>
        </div>

        {/* Main Content Grid */}
        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 lg:gap-12 max-w-6xl mx-auto items-start">
          {/* Left Column - Vertical Agent Flow (4/12) */}
          <div className="flex-shrink-0 w-full lg:w-auto lg:ml-36">
            <div className="sticky top-8">
              {/* Vertical Agent Flow */}
              <div className="relative mb-6 sm:mb-8">
                <div className="flex flex-row lg:flex-col items-center lg:items-center justify-start lg:justify-start space-x-2 sm:space-x-4 lg:space-x-0 lg:space-y-6 overflow-x-auto lg:overflow-x-visible pt-4 pb-4 lg:pb-0 px-2 lg:px-0">
                  {stages.map((stage, index) => (
                    <div key={stage.id} className="relative flex flex-col lg:flex-row items-center w-auto lg:w-full flex-shrink-0">
                      {/* Vertical Line (except for last item) */}
                      {index < stages.length - 1 && (
                        <div className="hidden lg:block absolute left-8 top-16 w-0.5 h-6 bg-card-border">
                          <div 
                            className={`
                              w-full bg-gradient-salespad-relay transition-all duration-700 ease-out
                              ${index < currentStage ? 'h-full shadow-glow-blue' : 'h-0'}
                            `}
                          />
                        </div>
                      )}

                      {/* Agent Dot and Label */}
                      <div className="flex flex-col lg:flex-row items-center lg:space-x-4 w-auto lg:w-full">
                        {/* Agent Dot */}
                        <button
                          onClick={() => handleStageClick(index)}
                          className={`
                            w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full transition-all duration-500 cursor-pointer relative flex items-center justify-center overflow-hidden flex-shrink-0
                            ${index === currentStage 
                              ? 'bg-gradient-salespad-relay shadow-glow-combined scale-125 animate-pulse' 
                              : index < currentStage
                                ? 'bg-brand-green-teal shadow-chip-glow scale-105'
                                : 'bg-card-border hover:bg-brand-blue/50 hover:scale-105'
                            }
                          `}
                        >
                          {/* Agent Avatar or Icon */}
                          {index === currentStage ? (
                            <img
                              src={`/landing-page${stage.image}`}
                              alt={stage.name}
                              className="w-8 h-8 sm:w-12 sm:h-12 lg:w-14 lg:h-14 object-cover rounded-full animate-pop-from-center"
                              key={`avatar-${stage.id}`}
                            />
                          ) : (
                            <div className={`
                              transition-all duration-500
                              ${index < currentStage
                                ? 'text-white'
                                : 'text-text-secondary'
                              }
                            `}>
                              <div className="w-4 h-4 sm:w-4 sm:h-4">
                                {getAgentIcon(index)}
                              </div>
                            </div>
                          )}

                          {/* Inner glow for active state */}
                          {index === currentStage && (
                            <>
                              <div className="absolute inset-0 rounded-full bg-gradient-salespad-relay opacity-60 animate-ping" />
                              <div className="absolute inset-0 rounded-full border-2 border-brand-blue/30 animate-ping" style={{ animationDelay: '0.5s' }} />
                            </>
                          )}
                        </button>

                        {/* Agent Label - Hidden on mobile, shown on desktop */}
                        <div className="hidden lg:block mt-2 lg:mt-0 text-center lg:text-left min-w-0">
                          <div className="text-text-primary text-sm font-medium">
                            {stage.name}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Action Panel (8/12) */}
          <div className="flex-1 lg:mr-16">
            {/* Enhanced Agent Action Visualizer */}
            <div className="bg-bg-surface border border-card-border rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 shadow-glow-combined card-inner-glow relative overflow-hidden">
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-brand-green-teal/5 rounded-xl sm:rounded-2xl" />
              
              <div className="relative z-10">
                {/* Agent Details Header */}
                <div className="mb-4 sm:mb-5 lg:mb-6">
                  <h3 className="text-text-primary font-bold text-lg sm:text-xl mb-2">
                    {currentAgent.name}
                  </h3>
                  <p className="text-text-secondary text-sm sm:text-base">
                    {currentAgent.oneLiner}
                  </p>
                </div>

                <AgentActionVisualizer currentAgent={currentAgent} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;